import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, Send, Loader2, Trash2, GripVertical } from "lucide-react";

const WELCOME = {
  role: "assistant",
  text: "Hoi! Ik ben Larissa, de kennisassistent van Clazing. Vraag me alles over onze producten, bereiding (frituur, oven, airfryer), houdbaarheid, bewaren, de wetgeving voor snackproducenten — of hoe je iets invult in het CRM-systeem.",
};

const newSession = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const POS_KEY = "clazing_bot_pos";

export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(newSession);
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(POS_KEY)); if (s && typeof s.x === "number" && typeof s.y === "number") return s; } catch (e) { /* ignore */ }
    return null; // null = default bottom-right
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const drag = useRef({ dragging: false, moved: false, startX: 0, startY: 0, offX: 0, offY: 0 });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("open-larissa", openIt);
    return () => window.removeEventListener("open-larissa", openIt);
  }, []);

  // keep widget inside the viewport when it resizes
  useEffect(() => {
    const clamp = () => {
      setPos((p) => {
        if (!p || !wrapRef.current) return p;
        const w = wrapRef.current.offsetWidth, h = wrapRef.current.offsetHeight;
        return { x: Math.max(6, Math.min(window.innerWidth - w - 6, p.x)), y: Math.max(6, Math.min(window.innerHeight - h - 6, p.y)) };
      });
    };
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, []);

  const onDown = (e) => {
    const el = wrapRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    drag.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, offX: e.clientX - r.left, offY: e.clientY - r.top };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  };
  const onMove = (e) => {
    const d = drag.current; if (!d.dragging) return;
    if (Math.abs(e.clientX - d.startX) > 4 || Math.abs(e.clientY - d.startY) > 4) d.moved = true;
    const el = wrapRef.current; if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight;
    const x = Math.max(6, Math.min(window.innerWidth - w - 6, e.clientX - d.offX));
    const y = Math.max(6, Math.min(window.innerHeight - h - 6, e.clientY - d.offY));
    setPos({ x, y });
  };
  const onUp = (e) => {
    const d = drag.current; if (!d.dragging) return;
    d.dragging = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    setPos((p) => { if (p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch (err) { /* ignore */ } } return p; });
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/sales/chatbot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("clazing_token")}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("fout");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Er ging iets mis. Probeer het later opnieuw." };
        return copy;
      });
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // positioning: custom pos (top-left) OR default bottom-right
  const style = pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : { right: 20, bottom: 20 };

  return (
    <div ref={wrapRef} className="fixed z-40" style={{ ...style, touchAction: "none" }} data-testid="chatbot-widget">
      {!open && (
        <button
          data-testid="chatbot-open-btn"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={(e) => { onUp(e); if (!drag.current.moved) setOpen(true); }}
          className="flex select-none items-center gap-2 rounded-full bg-blue-700 px-5 py-3 text-white shadow-lg shadow-blue-900/25 transition-colors hover:bg-blue-800 active:scale-95 cursor-grab active:cursor-grabbing"
          title="Sleep om te verplaatsen · klik om te openen"
        >
          <img src="/larissa-avatar.jpg" alt="Larissa" className="h-8 w-8 rounded-full object-cover ring-2 ring-white/70" />
          <span className="text-sm font-semibold">Larissa</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="flex h-[560px] max-h-[85vh] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <div
            data-testid="chatbot-drag-handle"
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            className="flex items-center justify-between bg-blue-700 px-4 py-3 text-white cursor-grab active:cursor-grabbing select-none"
          >
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-blue-200" />
              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white/15 ring-2 ring-white/40">
                <img src="/larissa-avatar.jpg" alt="Larissa" className="h-full w-full object-cover" />
              </div>
              <div>
                <div className="text-sm font-bold leading-tight">Larissa</div>
                <div className="text-[11px] text-blue-100">Clazing kennisassistent · sleep om te verplaatsen</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="chatbot-clear-btn"
                title="Gesprek wissen"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setMessages([WELCOME])}
                className="rounded-md p-1.5 hover:bg-white/15"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                data-testid="chatbot-close-btn"
                title="Sluiten"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 hover:bg-white/15"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-blue-700 text-white"
                      : "rounded-bl-sm border border-slate-200 bg-white text-slate-800"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Vraag Larissa over producten, bereiding, wetgeving of het CRM..."
                className="max-h-28 min-h-[42px] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <Button
                data-testid="chatbot-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                className="h-[42px] w-[42px] shrink-0 rounded-xl bg-blue-700 p-0 hover:bg-blue-800"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;
